import { useEffect, useState } from 'react';
import axios from 'axios';
import { useVenue } from '../contexts/VenueContext';
import { fetchSelectedVenueApplication } from '../utils/venueAppApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MIN_PHOTOS = 10;

// Mirrors the setup-mode checklist (`VenueSetupModeHome.js`) so the
// Settings page and Profile menu stay in lockstep with that source of
// truth: as soon as the owner uncompletes a required item (deletes
// photos, clears their vibe, wipes hours, or detaches the payout) the
// badges reappear here — because the venue drops off the guest map
// (setup_mode) or into on-hold (approved) until it's fixed.
function hasAnyOperatingHours(hours) {
  if (!hours || typeof hours !== 'object') return false;
  return Object.values(hours).some((v) => typeof v === 'string' && v.trim().length > 0);
}

/**
 * Per-settings-row "is this required step done?" signal.
 *
 * Returns a `steps` map keyed by the SAME key used by the settings row
 * (`photos`, `card`, `hours`, `payouts`). Keys are only populated once
 * the venue application fetch (and payout fetch, for approved venues)
 * resolves — before that `steps` is empty so pages never flash red
 * badges on first paint while the requests are in flight.
 *
 * Payouts behave slightly differently from the three profile steps:
 * they're only treated as a required-and-missing step for `approved`
 * venues on-hold (no payout attached). Setup-mode venues haven't
 * reached the payout stage yet, and `live` venues already have one —
 * neither state paints a badge on the Payouts row.
 */
export function useVenueRequiredSteps() {
  const { activeVenueAppId, activeVenue } = useVenue();
  const [application, setApplication] = useState(null);
  const [appLoaded, setAppLoaded] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [payoutLoaded, setPayoutLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAppLoaded(false);
    (async () => {
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (cancelled) return;
        setApplication(app);
      } catch {
        if (!cancelled) setApplication(null);
      } finally {
        if (!cancelled) setAppLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  // Payouts only matter once the venue is approved: setup_mode owners
  // haven't connected a payout yet, and `live` venues already did.
  const venueStatus = activeVenue?.status;
  useEffect(() => {
    if (!activeVenueAppId || venueStatus !== 'approved') {
      setPayoutStatus(null);
      setPayoutLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setPayoutLoaded(false);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (!cancelled) setPayoutLoaded(true);
          return;
        }
        const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPayoutStatus(res.data || null);
      } catch {
        if (!cancelled) setPayoutStatus(null);
      } finally {
        if (!cancelled) setPayoutLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId, venueStatus]);

  const ready = appLoaded && application !== null && payoutLoaded;

  const photoCount = Array.isArray(application?.photo_urls) ? application.photo_urls.length : 0;
  const categoryCount = Array.isArray(application?.venue_categories) ? application.venue_categories.length : 0;
  const hasDescription = Boolean((application?.map_description || '').trim());
  const tagCount = Array.isArray(application?.venue_tags) ? application.venue_tags.length : 0;
  const hoursSet = hasAnyOperatingHours(application?.operating_hours);

  const photosDone = photoCount >= MIN_PHOTOS;
  const cardDone = categoryCount > 0 && hasDescription && tagCount > 0;
  // Payouts: only enforce for approved+on-hold venues. For every other
  // status this row is simply not part of the required set, so it never
  // paints a red badge.
  const payoutsApplicable = venueStatus === 'approved';
  const payoutsDone = !payoutsApplicable || payoutStatus?.is_on_hold !== true;

  // `steps` ONLY carries real signals once we've resolved both fetches.
  // Before that it's an empty object, so page-level lookups treat each
  // row as "no judgement yet" and no red badge is painted on first
  // paint of a fully-set venue.
  const steps = ready ? {
    photos: photosDone,
    card: cardDone,
    hours: hoursSet,
    ...(payoutsApplicable ? { payouts: payoutsDone } : {}),
  } : {};

  const details = ready ? {
    photos: {
      done: photosDone,
      count: photoCount,
      min: MIN_PHOTOS,
      remaining: Math.max(0, MIN_PHOTOS - photoCount),
    },
    card: {
      done: cardDone,
      categoryCount,
      tagCount,
      hasDescription,
    },
    hours: {
      done: hoursSet,
    },
    payouts: {
      done: payoutsDone,
      applicable: payoutsApplicable,
      onHold: payoutStatus?.is_on_hold === true,
    },
  } : {};

  const missingKeys = Object.keys(steps).filter((k) => !steps[k]);
  const hasMissing = ready && missingKeys.length > 0;

  return { steps, details, missingKeys, hasMissing, loaded: ready };
}

export default useVenueRequiredSteps;
